import {
  ComponentPropsWithoutRef,
  FormEventHandler,
  Fragment,
  useMemo,
  useState,
} from 'react'

import { Button } from '@/components/ui/button'
import { graphql } from '@/gql/generated'
import { SurveyQuestionType } from '@/gql/generated/graphql'
import { useQuery } from '@/hooks/useQuery'
import { cn } from '@/utils/tw-helpers'
import { MultipleChoicesQuestion } from './MultipleChoicesQuestion'
import { SingleChoiceQuestion } from './SingleChoiceQuestion'
import { TextQuestion } from './TextQuestion'
import { TChoiceStyle, TSurveyResponse } from './types'

interface Props extends Omit<ComponentPropsWithoutRef<'form'>, 'onSubmit'> {
  surveyName: string
  submitText?: string
  titleClassName?: string
  descriptionClassName?: string
  labelClassName?: string
  choiceStyle?: TChoiceStyle
  defaultValues?: TSurveyResponse[] | null
  disabled?: boolean
  onSubmit: ({
    surveyId,
    responses,
  }: {
    surveyId: string
    responses: TSurveyResponse[]
  }) => void
}

export const SurveyForm: React.FC<Props> = ({ defaultValues, ...rest }) => {
  return (
    defaultValues !== null && (
      <SurveyFormInner defaultValues={defaultValues} {...rest} />
    )
  )
}

export const SurveyFormInner: React.FC<Props> = ({
  surveyName,
  submitText = '回答する',
  titleClassName,
  descriptionClassName,
  labelClassName,
  choiceStyle = 'normal',
  defaultValues,
  disabled: externalDisabled,
  onSubmit,
  ...rest
}) => {
  const [{ data }] = useQuery({
    query: SurveyForm_Query,
    variables: {
      name: surveyName,
    },
  })
  const survey = data?.survey

  const [responses, setResponses] = useState<TSurveyResponse[]>(
    defaultValues || []
  )

  const requiredQuestionIds = useMemo(
    () =>
      survey?.surveyQuestions
        ?.filter((question) => question.required)
        .map((question) => question.id) || [],
    [survey]
  )

  const disabled = useMemo(() => {
    if (externalDisabled) return true
    if (requiredQuestionIds.length === 0) return false

    return requiredQuestionIds.some(
      (questionId) => !responses.map((r) => r.questionId).includes(questionId)
    )
  }, [externalDisabled, requiredQuestionIds, responses])

  const handleSubmit: FormEventHandler<HTMLFormElement> = (evt) => {
    evt.preventDefault()

    if (!survey) return

    onSubmit({
      surveyId: survey.id,
      responses,
    })
  }

  return (
    <>
      <h3
        className={cn(
          'mb-[30px] text-center text-[32px] font-bold text-secondary-20 md:mb-[26px] md:text-2xl',
          titleClassName
        )}
      >
        {survey?.title}
      </h3>
      <p
        className={cn(
          'mx-auto mb-[30px] max-w-[544px] text-center text-secondary-20',
          descriptionClassName
        )}
      >
        {survey?.description}
      </p>
      <form
        onSubmit={handleSubmit}
        className="mx-auto max-w-[544px] rounded-xl bg-white p-7"
        {...rest}
      >
        <div className="flex flex-col gap-y-9">
          {survey?.surveyQuestions?.map((question) => (
            <Fragment key={question.id}>
              {question.questionType === SurveyQuestionType.Text && (
                <TextQuestion
                  question={question}
                  labelClassName={labelClassName}
                  defaultValue={
                    defaultValues?.find((r) => r.questionId === question.id)
                      ?.textAnswer
                  }
                  onValueChange={(answer) => {
                    const otherResponses = responses.filter(
                      (r) => r.questionId !== question.id
                    )

                    const newResponses = {
                      questionId: question.id,
                      textAnswer: answer.value,
                    }

                    setResponses([...otherResponses, newResponses])
                  }}
                />
              )}
              {question.questionType === SurveyQuestionType.MultipleChoices && (
                <MultipleChoicesQuestion
                  question={question}
                  labelClassName={labelClassName}
                  choiceStyle={choiceStyle}
                  defaultValue={defaultValues
                    ?.filter((r) => r.questionId === question.id)
                    .map((r) => r.choiceId)
                    .filter((id): id is string => id !== undefined)}
                  onValueChange={(answer) => {
                    const choiceIds = answer.value.split(',')

                    const otherResponses = responses.filter(
                      (r) => r.questionId !== question.id
                    )

                    const newResponses = choiceIds
                      .filter(Boolean) // Remove empty strings
                      .map((choiceId) => ({
                        questionId: question.id,
                        choiceId,
                      }))

                    setResponses([...otherResponses, ...newResponses])
                  }}
                />
              )}
              {question.questionType === SurveyQuestionType.SingleChoice && (
                <SingleChoiceQuestion
                  question={question}
                  labelClassName={labelClassName}
                  choiceStyle={choiceStyle}
                  defaultValue={
                    defaultValues?.find((r) => r.questionId === question.id)
                      ?.choiceId
                  }
                  onValueChange={(answer) => {
                    const otherResponses = responses.filter(
                      (r) => r.questionId !== question.id
                    )

                    const newResponses = {
                      questionId: question.id,
                      choiceId: answer.value,
                    }

                    setResponses([...otherResponses, newResponses])
                  }}
                />
              )}
            </Fragment>
          ))}
        </div>

        <div className="mt-[30px] flex justify-center">
          <Button
            type="submit"
            variant="secondary"
            size="lg"
            className="h-[72px] w-[244px] rounded-3xl text-[25px] text-orange-20 shadow-medium"
            disabled={disabled}
          >
            {submitText}
          </Button>
        </div>
      </form>
    </>
  )
}

const SurveyForm_Query = graphql(`
  query SurveyForm_Query($name: String!) {
    survey(name: $name) {
      id
      title
      description
      surveyQuestions {
        id
        question
        questionType
        displayOrder
        required
        surveyQuestionChoices {
          id
          choiceText
          displayOrder
        }
      }
    }
  }
`)
